import { useEffect, useRef, useState } from 'react';
import { Camera, Images, X } from 'lucide-react';
import './scan-source-chooser.css';

type PendingInput = {
  input: HTMLInputElement;
  multiple: boolean;
  accept: string;
  capture: string | null;
};

function shouldUseCustomSourceChooser() {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent;
  const isMobileLike = window.matchMedia('(pointer: coarse)').matches || /Android|iPhone|iPad|iPod/i.test(userAgent);
  if (!isMobileLike) return false;

  // Firefox and iOS already provide a useful native camera/photo-source chooser.
  // Adding PlantGrow's chooser there causes the user to make the same choice twice.
  if (/Firefox|FxiOS/i.test(userAgent)) return false;
  if (/iPhone|iPad|iPod/i.test(userAgent)) return false;

  // Use the explicit PlantGrow source sheet on Android Chromium-family browsers,
  // where a plain image input can otherwise jump straight to Files.
  return /Android/i.test(userAgent) && /(Chrome|Chromium|EdgA|OPR|SamsungBrowser)/i.test(userAgent);
}

export function ScanSourceChooser() {
  const [pending, setPending] = useState<PendingInput | null>(null);
  const bypassOnce = useRef(new WeakSet<HTMLInputElement>());

  useEffect(() => {
    if (!shouldUseCustomSourceChooser()) return;

    const intercept = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type !== 'file') return;
      if (!target.accept.toLowerCase().includes('image')) return;

      if (bypassOnce.current.has(target)) {
        bypassOnce.current.delete(target);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setPending({
        input: target,
        multiple: target.multiple,
        accept: target.accept,
        capture: target.getAttribute('capture'),
      });
    };

    document.addEventListener('click', intercept, true);
    return () => document.removeEventListener('click', intercept, true);
  }, []);

  function openSource(source: 'camera' | 'library') {
    if (!pending) return;
    const { input, multiple, accept, capture } = pending;
    setPending(null);

    if (source === 'camera') {
      input.multiple = false;
      input.accept = 'image/*';
      input.setAttribute('capture', 'environment');
    } else {
      input.multiple = multiple;
      input.accept = accept || 'image/*';
      input.removeAttribute('capture');
    }

    bypassOnce.current.add(input);
    input.click();

    // Chromium reads capture/multiple at picker launch. Restore the app's original
    // input configuration immediately afterwards so subsequent scans behave the same.
    window.setTimeout(() => {
      input.multiple = multiple;
      input.accept = accept;
      if (capture === null) input.removeAttribute('capture');
      else input.setAttribute('capture', capture);
    }, 0);
  }

  if (!pending) return null;

  return (
    <div className="scan-source-backdrop" role="presentation" onClick={() => setPending(null)}>
      <section
        className="scan-source-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scan-source-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="scan-source-heading">
          <div>
            <span>Scan plant</span>
            <h2 id="scan-source-title">Choose a photo source</h2>
          </div>
          <button type="button" onClick={() => setPending(null)} aria-label="Close photo source chooser">
            <X size={20} />
          </button>
        </div>

        <div className="scan-source-actions">
          <button type="button" className="scan-source-action primary" onClick={() => openSource('camera')}>
            <span className="scan-source-icon"><Camera size={24} /></span>
            <span><strong>Take a photo</strong><small>Open your rear camera</small></span>
          </button>
          <button type="button" className="scan-source-action" onClick={() => openSource('library')}>
            <span className="scan-source-icon"><Images size={24} /></span>
            <span><strong>Choose photos</strong><small>Select existing images from your phone</small></span>
          </button>
        </div>
      </section>
    </div>
  );
}
